import { useEffect, useMemo, useState } from 'react';

import type { IDayObject, IView } from './types';

import useBreakpoints from '@snowit/hooks/useBreakpoints';
import useLanguage from '@snowit/system/useLanguage';
import { Flex } from '@snowit/ui';
import executer from '@snowit/utils/executer';
import snowmentV2 from '@snowit/utils/snowmentv2';
import testId from '@snowit/utils/testId';

import Button from '../../Buttons/Button';
import ButtonIcon from '../../Buttons/ButtonIcon';
import Loader from '../../Loaders/LoaderSpinner';
import Typo from '../../Typography/Typo';
import CalendarDay from './CalendarDay';
import CalendarWeekdays from './CalendarWeekdays';
import useCalendar from './useCalendar';

import cx from 'classnames';
import s from './Calendar.module.scss';

export interface ICalendar {
  activeStartDate?: string;
  className?: string;
  double?: boolean;
  loading?: boolean;
  maxDate?: string;
  maxDays?: number | null;
  minDate?: string;
  minDays?: number;
  mode?: 'range' | 'single';
  onChange: (date: string) => void;
  onChangeView?: (date: string) => void;
  onlyDates?: string[];
  test?: string;
  value: string[];
  availability?: Record<string, string>;
}

const Calendar = (props: ICalendar) => {
  const { t } = useLanguage();
  const {
    activeStartDate,
    className,
    double = false,
    loading,
    maxDate,
    minDate,
    onChange,
    onChangeView,
    onlyDates,
    test,
    value,
    availability,
  } = props;

  const { breakpoints, minWidth } = useBreakpoints();

  const values = (Array.isArray(value) ? value : [value]).filter(Boolean);
  const first = values[0];
  const last = values[values.length - 1];

  const ranges = useMemo(() => {
    // get the full range
    const fullRange = snowmentV2.enumerateDays(first, last);
    // set an index to start with
    let index = 0;
    // a = all, c = current, i = index, o = original
    return fullRange.reduce((a: string[][], c: string, i: number, o: string[]) => {
      // get the previous date in the array (if exists)
      const prev = o[i - 1];
      // the the enxt date in the array (if exists)
      const next = o[i + 1];
      // if values do not include the previous, but includes the value
      // set the new index to slice the array with
      if (!values.includes(prev) && values.includes(c)) {
        index = i;
      }
      // if the previous exists and is included into the values,
      // but the current one is out of range, slice the array
      if (prev && values.includes(prev) && !values.includes(c)) {
        return [...a, o.slice(index, i)];
      }
      // if there is no next one it means we are at the end of the array
      if (!next && values.includes(c)) {
        return [...a, o.slice(index, i + 1)];
      }
      // otherwise do nothing and return what we have
      return a;
    }, [] as string[][]);
  }, [values, first, last]);

  const [view, setView] = useState<string>(
    first || activeStartDate || minDate || snowmentV2().format(),
  );

  useEffect(() => {
    if (activeStartDate) {
      setView(activeStartDate);
    }
  }, [activeStartDate]);

  const { current, next, weekdays, months } = useCalendar(view, { double });

  const isDayDisabled = (day: IDayObject, onlyVisual: boolean) => {
    const { value: d } = day;
    if (maxDate && snowmentV2(maxDate).isBefore(d, 'day')) {
      return true;
    }
    if (minDate && snowmentV2(minDate).isAfter(d, 'day')) {
      return true;
    }
    if (onlyVisual && Array.isArray(value) && value.includes(d)) return false;
    if (Array.isArray(onlyDates) && !onlyDates.includes(d)) {
      return true;
    }
    return false;
  };

  const handleDayClick = (day: IDayObject) => {
    if (isDayDisabled(day, false)) return;
    onChange(day.value);
  };

  const onPrevMonth = () => {
    const newDate = snowmentV2(view).subtract(1, 'months').startOf('month').toFormat('yyyy-MM-dd');
    setView(newDate);
    executer(onChangeView, newDate);
  };

  const onNextMonth = () => {
    const newDate = snowmentV2(view).add(1, 'months').startOf('month').toFormat('yyyy-MM-dd');
    setView(newDate);
    executer(onChangeView, newDate);
  };

  const renderMonthName = (month: string, year: number) => {
    const today = snowmentV2();
    const currentMonth = today.year === year && today.month === months.indexOf(month) + 1;

    const name = t(`lzy.time.full_month.${month}`);

    return (
      <Flex align={'baseline'} gap={0.25}>
        <Typo
          text={name}
          tag={'p'}
          size={'df'}
          weight={currentMonth ? 'medium' : 'regular'}
          color={'text'}
        />
        <Typo text={year} tag={'p'} size={'df'} weight={'regular'} color={'text-l'} />
      </Flex>
    );
  };

  const renderHeader = (thisView: IView, nextView?: IView) => {
    if ((breakpoints.xs && double) || !thisView) return null;
    const { month, year } = thisView;

    return (
      <div className={s.header}>
        <ButtonIcon
          classNames={{ button: s.prevBtnIcon }}
          icon={'arrow_angle_left'}
          onClick={onPrevMonth}
          theme={'ghost'}
          size={'db'}
          color={'text'}
          a11y={{ label: 'Previous month' }}
          test={'prev-month-icon'}
        />
        <div className={cx(s.wrapTitle, { [s.prevMonth]: double })}>
          {renderMonthName(month, year)}
        </div>
        {double && nextView && (
          <div className={cx(s.wrapTitle, { [s.nextMont]: double })}>
            {renderMonthName(nextView.month, nextView.year)}
          </div>
        )}
        <ButtonIcon
          classNames={{ button: s.nextBtnIcon }}
          icon={'arrow_angle_right'}
          onClick={onNextMonth}
          theme={'ghost'}
          size={'db'}
          color={'text'}
          a11y={{ label: 'Next month' }}
          test={'next-month-icon'}
        />
      </div>
    );
  };

  const renderLoading = () => {
    if (!loading) return null;
    return (
      <div className={s.loaderOverlay}>
        <Loader className={s.loaderIcon} size={24} />
        <p className={s.loaderLabel}>{t('lzy.loading.check_availability.text')}</p>
      </div>
    );
  };

  const renderBlockName = (month: string, year: number) => {
    if (minWidth.sm || !double) return null;
    return <div className={cx(s.month)}>{renderMonthName(month, year)}</div>;
  };

  const renderWeekdays = () => {
    return <CalendarWeekdays weekdays={weekdays} />;
  };

  const renderCell = (day: IDayObject) => {
    const disabled = isDayDisabled(day, true);
    return (
      <CalendarDay
        key={day.value}
        day={day}
        onClick={handleDayClick}
        ranges={ranges}
        disabled={disabled}
        availability={availability?.[day.value]}
      />
    );
  };

  const renderDaysCells = (days: IDayObject[]) => {
    return <div className={s.monthDays}>{days.map(renderCell)}</div>;
  };

  const renderDayBlocks = (daysView?: IView) => {
    if (!daysView) return null;
    const { days, month, year } = daysView;

    return (
      <div className={s.dayBlock}>
        {renderLoading()}
        {renderBlockName(month, year)}
        {renderWeekdays()}
        {renderDaysCells(days)}
      </div>
    );
  };

  const renderMobileNavigation = (_type: 'prev' | 'next') => {
    if (minWidth.sm || !double) return null;

    return (
      <Button
        classNames={{ button: s.btn }}
        label={t(`lzy.action.month_${_type}`)}
        icon={_type === 'prev' ? 'arrow_long_up' : 'arrow_long_down'}
        theme={'outline'}
        bgColor={'text'}
        onClick={_type === 'prev' ? onPrevMonth : onNextMonth}
        a11y={{ label: _type === 'prev' ? 'Previous month' : 'Next month' }}
        test={`${test}-${_type}-month`}
      />
    );
  };

  const renderContent = () => {
    return (
      <div className={s.wrapDayBlock}>
        {renderMobileNavigation('prev')}
        {renderDayBlocks(current)}
        {renderDayBlocks(next)}
        {renderMobileNavigation('next')}
      </div>
    );
  };

  return (
    <div className={cx(s.calendar, className, { [s.double]: double })} {...testId(test)}>
      {renderHeader(current, next)}
      {renderContent()}
    </div>
  );
};

export default Calendar;
